import React, { useEffect, useMemo, useState } from 'react';
import { Column, Row } from 'react-table';

import { AdminCouponDto, AdminVoucherDto, UserInfoDto } from 'services/data-contracts';
import TableBody from 'components/table/TableBody';
import { EditButton } from 'resources/styles/TableStyle';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import PaginationWrapper from '../Pagination';
import { useStoreWrapper } from 'stores/StoreHelper';
import CouponFilterStore from 'stores/CouponFilterStore';

interface CouponTableProps {
  coupons: AdminCouponDto[];
  onRowClick?: (voucherGroup: AdminCouponDto) => void;
  onEditClick?: (voucherGroup: AdminCouponDto) => void;
}


// 테이블 데이터 타입 정의
const CouponTable = observer(({ coupons, onRowClick, onEditClick }: CouponTableProps) => {
  const { couponFilterStore } = useStoreWrapper();

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'id' as keyof AdminCouponDto,
        width: 60,
      },
      {
        Header: '할인유형',
        accessor: 'discountType' as keyof AdminCouponDto,
        width: 100,
        Cell: ({ value }: { value: string }) => {
          return value === 'FIXED' ? '정액할인' : '비율할인';
        },
      },

      {
        Header: '한옥',
        accessor: 'listingSummary' as keyof AdminCouponDto,
        width: 200,

      },
      {
        Header: '쿠폰명',
        accessor: 'localizedName' as keyof AdminCouponDto,
        width: 200,
        Cell: ({ value }: { value: Record<string, string> }) => {
          return value ? value?.['ko'] : '-';
        },
      },
      {
        Header: '설명',
        accessor: 'localizedDesc' as keyof AdminCouponDto,
        width: 200,
        Cell: ({ value }: { value: Record<string, string> }) => {
          return <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value ? value?.['ko'] : '-'}</div>;
        },
      },
      {
        Header: '할인금액',
        accessor: 'price' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value, row }: { value: number, row: any }) => {
          const discountType = row.original.discountType;
          return value ? `${value.toLocaleString()}${discountType === 'FIXED' ? '원' : '%'}` : '0';
        },
      },
      {
        Header: '숙박기간',
        accessor: 'stayStartDate' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value, row }: { value: string, row: any }) => {
          const stayStartDate = row.original.stayStartDate;
          const stayEndDate = row.original.stayEndDate;
          return value ? `${new Date(stayStartDate).toLocaleDateString('ko-KR')} ~ ${new Date(stayEndDate).toLocaleDateString('ko-KR')}` : '-';
        },
      },

      {
        Header: '만료여부',
        accessor: 'expired' as keyof AdminCouponDto,
        width: 80,
        Cell: ({ value, row }: { value: boolean, row: any }) => {
          const expiredDate = row.original.expiredDate;
          const isExpired = expiredDate && new Date(expiredDate) < new Date();

          if (isExpired) return '만료';
          return value ? '만료됨' : '활성';
        },
      },
      {
        Header: '만료일',
        accessor: 'expireDate' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
        },
      },
      {
        Header: '생성자',
        accessor: 'creator' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value }: { value: any }) => {
          return value ? value.username : '-';
        },
      },
      {
        Header: '생성일',
        accessor: 'createDate' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
        },
      },
      {
        Header: '수정자',
        accessor: 'modifier' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value }: { value: any }) => {
          return value ? value.username : '-';
        },
      },
      {
        Header: '수정일',
        accessor: 'modifyDate' as keyof AdminCouponDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
        },
      },

      {
        Header: '수정',
        id: 'edit',
        width: 80,
        Cell: ({ row }: { row: Row<AdminCouponDto> }) => <EditButton onClick={(e) => {
          e.stopPropagation();
          onEditClick?.(row.original as AdminCouponDto);
        }}>수정</EditButton>,
      },
    ],
    [],
  ) as Column<UserInfoDto>[];



  return (
    <div>
      <div style={{ paddingBottom: '100px' }}>
        <TableBody columns={columns} data={coupons} onRowClick={onRowClick} />
      </div>
      <PaginationContainer>
        <PaginationWrapper count={couponFilterStore?.paginationInfo?.totalCount ?? 0}
          totalPages={couponFilterStore?.paginationInfo?.totalPages ?? 0} />
      </PaginationContainer>
    </div>
  );
});

const PaginationContainer = styled.div`
    margin-top: 20px;
    position:fixed;
    bottom:0;
    z-index:10;
    margin:auto;
    background:rgba(255,255,255,1);
    backdrop-filter: blur(10px);
    left:0;
    right:0;

    display:flex;
    justify-content:center;
    align-items:center;

`;

export default CouponTable;
